// src/components/ServiceRecovery/InstantFeedbackForm.js
import { CheckCircle2, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, PrimaryButton } from '../ui';
import serviceRecoveryApi from './serviceRecoveryApi';

const InstantFeedbackForm = ({ orderId, siteId: propSiteId, onSuccess }) => {
  // Log screen view
  useScreenLogging('InstantFeedbackForm', {
    component: 'InstantFeedbackForm',
    screenType: 'feedback-form',
  });

  const [selectedSite, setSelectedSite] = useState(propSiteId || '');
  const [orderIdInput, setOrderIdInput] = useState(orderId || '');
  const [guestName, setGuestName] = useState('');
  const [orderItemOptions, setOrderItemOptions] = useState([]); // { order_item_id, label } for dropdown
  const [selectedOrderItemId, setSelectedOrderItemId] = useState('');
  const [loadingOrderItems, setLoadingOrderItems] = useState(false);
  const [sites, setSites] = useState([]);
  const [rating, setRating] = useState(null); // 1-5 stars
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [recoveryAction, setRecoveryAction] = useState('');
  const [requestCallback, setRequestCallback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  // Use new feedback categories matching API spec
  const categories = serviceRecoveryApi.getNewFeedbackCategories();

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userPermissions = await userService.getUserPermissions();
        setPermissions(userPermissions || []);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        setPermissions([]);
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    fetchPermissions();
  }, []);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites) ? response.sites : Array.isArray(response) ? response : [];
        setSites(list.sort((a, b) => (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')));
        
        // Auto-select prop site if provided
        if (propSiteId && list.length > 0) {
          setSelectedSite(propSiteId);
        } else if (list.length > 0 && !selectedSite) {
          setSelectedSite(list[0].site_id || list[0].id);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, [propSiteId]);

  // Fetch dish dataset with site_id and start_date/end_date; build order_item_id dropdown options
  // start_date = today 12:01 AM (00:00:00), end_date = current date and time
  useEffect(() => {
    if (!selectedSite) {
      setOrderItemOptions([]);
      setSelectedOrderItemId('');
      return;
    }
    setSelectedOrderItemId('');
    const fetchOrderItems = async () => {
      setLoadingOrderItems(true);
      try {
        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const startDate = startOfToday.toISOString();
        const endDate = now.toISOString();

        const data = await serviceRecoveryApi.getDishDataset(selectedSite, startDate, endDate);
        const list = Array.isArray(data) ? data : [];

        // Unique by order_item_id, with label = order_item_id + dish name when available
        const seen = new Set();
        const options = [];
        list.forEach((item) => {
          const id = item?.order_item_id;
          if (!id || seen.has(id)) return;
          seen.add(id);
          const dishName = item?.dish_info?.name;
          options.push({
            order_item_id: id,
            label: dishName ? `${id} — ${dishName}` : id,
          });
        });
        options.sort((a, b) => a.label.localeCompare(b.label));
        setOrderItemOptions(options);
      } catch (err) {
        console.error('Error fetching dish dataset for order items:', err);
        setOrderItemOptions([]);
      } finally {
        setLoadingOrderItems(false);
      }
    };
    fetchOrderItems();
  }, [selectedSite]);

  // Dismiss thank-you message after 3s and show Share Your Feedback form again
  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(() => handleReset(), 3000);
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedSite) {
      setError('Please select a site');
      return;
    }
    
    if (!orderIdInput || !orderIdInput.trim()) {
      setError('Please enter an order ID');
      return;
    }
    
    if (!selectedCategory) {
      setError('Please select a feedback category');
      return;
    }

    if (!rating) {
      setError('Please select a star rating');
      return;
    }

    if (!selectedOrderItemId || !selectedOrderItemId.trim()) {
      setError('Please select an order item');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Star rating (required): 1 star → -1, 2 → -0.5, 3 → 0, 4 → 0.5, 5 stars → 1
      const sentimentScore = (rating - 3) / 2;

      // Prepare feedback data according to new API spec
      // Pass selected Order Item (today's orders) as order_id when posting
      const feedbackData = {
        site_id: selectedSite,
        order_id: selectedOrderItemId.trim(),
        guest_name: guestName.trim() || undefined,
        order_item_id: selectedOrderItemId.trim() || undefined,
        feedback_text: feedbackText.trim() || undefined,
        feedback_category: selectedCategory,
        sentiment_score: sentimentScore,
        recovery_action_taken: recoveryAction.trim() || undefined,
        guest_sentiment_change_index: undefined, // Only set if recovery action was taken
      };

      // If recovery action was taken, set sentiment change index (optional)
      // COMMENTED OUT: Removed hardcoded default value - let API handle or calculate
      // if (recoveryAction.trim()) {
      //   // Default to 0.3 if not specified (can be made configurable)
      //   feedbackData.guest_sentiment_change_index = 0.3;
      // }

      console.log('📡 InstantFeedbackForm: Submitting feedback with data:', feedbackData);
      
      const result = await serviceRecoveryApi.createFeedback(feedbackData);
      console.log('📡 InstantFeedbackForm: Feedback created successfully:', result);

      // Log API event for creating feedback
      loggingService.logInfo(
        'feedback_create',
        `Created feedback for order ${selectedOrderItemId} at site ${selectedSite}`,
        {
          endpoint: '/feedback',
          method: 'POST',
          siteId: selectedSite,
          orderId: selectedOrderItemId,
          feedbackCategory: selectedCategory,
          rating: rating,
          hasFeedbackText: !!feedbackText.trim(),
          hasRecoveryAction: !!recoveryAction.trim(),
        }
      ).catch(logError => {
        console.warn('[InstantFeedbackForm] Failed to log event:', logError);
      });

      setIsSubmitted(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('❌ InstantFeedbackForm: Error submitting feedback:', err);
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setRating(null);
    setSelectedCategory(null);
    setGuestName('');
    setSelectedOrderItemId('');
    setFeedbackText('');
    setRecoveryAction('');
    setRequestCallback(false);
    setIsSubmitted(false);
    setError(null);
    // Keep site and order ID for resubmission
  };

  if (isSubmitted) {
    return (
      <Card.Gradient className="p-6 md:p-8 max-w-md mx-auto">
        <div className="text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '0.5rem' }}>
            Thank You!
          </h3>
          <p className={typography.p} style={{ color: '#000000', marginBottom: '1.5rem' }}>
            Your feedback has been submitted successfully.
          </p>

          {/* Would you like a callback? - commented out
          <div className="mb-4">
            <label className="flex items-center justify-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requestCallback}
                onChange={(e) => setRequestCallback(e.target.checked)}
                className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
              />
              <span className={typography.p} style={{ color: '#000000' }}>
                Would you like a callback?
              </span>
            </label>
          </div>
          {requestCallback && (
            <p className={typography.p} style={{ color: '#10B981', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              We'll contact you soon!
            </p>
          )}
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Submit Another Feedback
            </button>
          </div>
          */}

          <p className={typography.p} style={{ color: '#6B7280', fontSize: '0.875rem' }}>
            Returning to feedback form in 3 seconds...
          </p>
        </div>
      </Card.Gradient>
    );
  }

  return (
    <Card.Gradient className="p-6 md:p-8 max-w-md mx-auto">
      <h2 className={typography.h2} style={{ color: '#000000', marginBottom: '1rem', textAlign: 'center' }}>
        Share Your Feedback
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Site Selection */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Site <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            required
          >
            <option value="">Select Site</option>
            {sites.map((site) => (
              <option key={site.site_id || site.id} value={site.site_id || site.id}>
                {site.site_name || site.name}
              </option>
            ))}
          </select>
        </div>

        {/* Order ID */}
        {/* <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Order ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={orderIdInput}
            onChange={(e) => setOrderIdInput(e.target.value)}
            placeholder="Enter order ID"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            required
            disabled={!!orderId} // Disable if passed as prop
          />
        </div> */}

        {/* Order Item (from today's dish dataset: 12:01 AM — now) */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Order Item <span className="text-red-500">*</span>
            <span className="text-gray-500 text-sm ml-2">(today&apos;s orders)</span>
          </label>
          <select
            value={selectedOrderItemId}
            onChange={(e) => setSelectedOrderItemId(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            disabled={loadingOrderItems}
            required
          >
            <option value="">
              {loadingOrderItems ? 'Loading order items...' : 'Select order item'}
            </option>
            {orderItemOptions.map((opt) => (
              <option key={opt.order_item_id} value={opt.order_item_id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Guest Name (Optional) */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Guest Name
          </label>
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Enter guest name (optional)"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
          />
        </div>

        {/* Star Rating (required): 1 → -1, 3 → 0, 5 → 1 */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block', textAlign: 'center' }}>
            Rating <span className="text-red-500">*</span>
          </label>
          <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              className="focus:outline-none transition-transform hover:scale-110"
            >
              <Star
                className={`w-10 h-10 ${
                  rating && star <= rating
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
          </div>
        </div>

        {/* Category Chips */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Category
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.value}
                type="button"
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-full transition-all ${
                  selectedCategory === category.value
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <span className="mr-1">{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Feedback Text */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Feedback Text
            <span className="text-gray-500 text-sm ml-2">(Sentiment will be auto-analyzed if provided)</span>
          </label>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="The food was cold when served..."
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
          />
        </div>

        {/* Recovery Action (Optional) */}
        <div>
          <label className={typography.p} style={{ color: '#000000', marginBottom: '0.5rem', display: 'block' }}>
            Recovery Action Taken (Optional)
          </label>
          <textarea
            value={recoveryAction}
            onChange={(e) => setRecoveryAction(e.target.value)}
            placeholder="Offered complimentary dessert..."
            rows={3}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <PrimaryButton
          type="submit"
          disabled={!selectedSite || !orderIdInput || !selectedOrderItemId || !rating || !selectedCategory || isSubmitting}
          className="w-full"
        >
          {isSubmitting ? 'Sending...' : 'Send Feedback'}
        </PrimaryButton>
      </form>
    </Card.Gradient>
  );
};

export default InstantFeedbackForm;
